
import { ServerMessage, ClientMessage, GameActionV2Envelope } from '../types';
import { getWebSocketBase } from './runtimeConfig';

type MessageHandler = (message: ServerMessage) => void;
type StatusHandler = (status: 'CONNECTING' | 'CONNECTED' | 'DISCONNECTED' | 'ERROR' | 'RECONNECTING' | 'FAILED') => void;
type GameAckMessage = Extract<ServerMessage, { type: 'GAME_ACK' }>;

// Reliable delivery constants
const ACK_TIMEOUT_MS = 1500;   // wait for ACK before first retry
const ACK_MAX_ATTEMPTS = 5;    // total send attempts before giving up

interface PendingEntry {
  envelope: ClientMessage & { type: 'GAME_ACTION_V2' } & GameActionV2Envelope;
  attempts: number;
  timer: ReturnType<typeof setTimeout>;
  resolve: (ack: GameAckMessage) => void;
  reject: (reason: string) => void;
}

class WebSocketClient {
  private socket: WebSocket | null = null;
  private messageHandlers: MessageHandler[] = [];
  private statusHandlers: StatusHandler[] = [];
  public reconnectAttempts = 0; // Public for UI retry count
  private maxReconnectAttempts = 30;
  private reconnectDelay = 1000;
  private maxReconnectDelay = 10000;
  private url: string;
  private shouldReconnect = true;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private lastPongAt = 0;

  /** Pending reliable messages awaiting GAME_ACK keyed by actionId */
  private pendingMap = new Map<string, PendingEntry>();

  constructor(url: string = getWebSocketBase()) {
    this.url = url;
  }

  connect(): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.notifyStatus('CONNECTED');
      return;
    }

    if (this.socket?.readyState === WebSocket.CONNECTING) {
      return;
    }

    this.shouldReconnect = true;
    this.notifyStatus('CONNECTING');

    try {
      const socket = new WebSocket(this.url);
      this.socket = socket;

      socket.onopen = () => {
        if (this.socket !== socket) return;
        this.reconnectAttempts = 0;
        this.lastPongAt = Date.now();
        if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
        this.heartbeatTimer = setInterval(() => {
          if (this.socket !== socket || socket.readyState !== WebSocket.OPEN) return;
          if (Date.now() - this.lastPongAt > 60_000) {
            console.warn('[WS] Heartbeat timed out; closing stale connection');
            socket.close();
            return;
          }
          try {
            socket.send(JSON.stringify({ type: 'PING', sentAt: Date.now() }));
          } catch {
            socket.close();
          }
        }, 25_000);
        this.notifyStatus('CONNECTED');
        // Flush any pending reliable messages that were queued while disconnected
        this.pendingMap.forEach(entry => {
          clearTimeout(entry.timer);
          this._attemptSendReliable(entry);
        });
      };

      socket.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data) as ServerMessage;
          if (message.type === 'PONG') {
            this.lastPongAt = Date.now();
            return;
          }
          if ((message as any).forceDisconnect) {
            console.log('[WS] Force disconnect received based on server message.');
            this.shouldReconnect = false;
            this.socket?.close();
            localStorage.clear();
            window.location.href = '/'; // Forced reload/logout
            return;
          }

          // Handle ACK for reliable messages — consume here, don't forward to game handlers
          if (message.type === 'GAME_ACK') {
            const entry = this.pendingMap.get(message.actionId);
            if (entry) {
              clearTimeout(entry.timer);
              this.pendingMap.delete(message.actionId);
              if (message.accepted === false) {
                const reason = message.reason || 'Server rejected action';
                console.warn(`[WS] ACK rejected for ${message.actionId}: ${reason}`);
                entry.reject(reason);
              } else {
                console.log(`[WS] ACK received for ${message.actionId} (attempt ${entry.attempts}, stateVersion=${message.stateVersion ?? 'unknown'})`);
                entry.resolve(message);
              }
            }
            return;
          }

          this.messageHandlers.forEach(handler => handler(message));
        } catch { }
      };

      socket.onerror = () => {
        if (this.socket !== socket) return;
        this.notifyStatus('ERROR');
      };

      socket.onclose = () => {
        if (this.socket !== socket) return;
        if (this.heartbeatTimer) {
          clearInterval(this.heartbeatTimer);
          this.heartbeatTimer = null;
        }
        this.socket = null;
        this.notifyStatus('DISCONNECTED');

        if (this.shouldReconnect) {
          if (this.reconnectAttempts < this.maxReconnectAttempts) {
            this.handleReconnect();
          } else {
            this.notifyStatus('FAILED');
          }
        }
      };
    } catch {
      this.notifyStatus('ERROR');
    }
  }

  private handleReconnect(): void {
    this.reconnectAttempts++;
    this.notifyStatus('RECONNECTING');
    const delay = Math.min(this.maxReconnectDelay, this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1));

    setTimeout(() => {
      if (this.shouldReconnect) {
        this.connect();
      }
    }, delay);
  }

  disconnect(): void {
    this.shouldReconnect = false;
    this.reconnectAttempts = 0;
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    // Reject all pending reliable messages
    this.pendingMap.forEach(entry => {
      clearTimeout(entry.timer);
      entry.reject('WS disconnected by client');
    });
    this.pendingMap.clear();
    if (this.socket) {
      const socket = this.socket;
      this.socket = null;
      socket.close();
    }
    this.notifyStatus('DISCONNECTED');
  }

  /** Fire-and-forget send. Use for non-critical messages. */
  send(message: ClientMessage): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    }
  }

  /**
   * Reliable send for all multiplayer game actions.
   * Wraps the action in GAME_ACTION_V2 envelope, waits for GAME_ACK.
   * Retries up to ACK_MAX_ATTEMPTS times with exponential backoff starting at 1.5 seconds.
   * Resolves when ACK received, rejects after all retries exhausted.
   */
  sendReliable(envelope: ClientMessage & { type: 'GAME_ACTION_V2' } & GameActionV2Envelope): Promise<GameAckMessage> {
    return new Promise<GameAckMessage>((resolve, reject) => {
      // Deduplicate: if same actionId already pending, resolve immediately (idempotent)
      if (this.pendingMap.has(envelope.actionId)) {
        console.warn(`[WS] sendReliable: ${envelope.actionId} already pending, ignoring duplicate`);
        reject('Reliable action is already pending');
        return;
      }

      const entry: PendingEntry = {
        envelope,
        attempts: 0,
        timer: setTimeout(() => {}, 0), // placeholder, immediately overwritten
        resolve,
        reject,
      };

      this.pendingMap.set(envelope.actionId, entry);
      this._attemptSendReliable(entry);
    });
  }

  private _attemptSendReliable(entry: PendingEntry): void {
    if (this.socket?.readyState !== WebSocket.OPEN) {
      console.log(`[WS] sendReliable: socket not open, keeping ${entry.envelope.actionId} pending`);
      entry.timer = setTimeout(() => {
        if (!this.pendingMap.has(entry.envelope.actionId)) return;
        this._attemptSendReliable(entry);
      }, ACK_TIMEOUT_MS);
      return;
    }

    entry.attempts++;
    const updatedEnvelope = { ...entry.envelope, attempt: entry.attempts };
    entry.envelope = updatedEnvelope as any;
    this.socket.send(JSON.stringify(updatedEnvelope));
    console.log(`[WS] sendReliable: sent ${entry.envelope.actionId} (attempt ${entry.attempts})`);

    const backoff = ACK_TIMEOUT_MS * Math.pow(2, entry.attempts - 1);

    entry.timer = setTimeout(() => {
      if (!this.pendingMap.has(entry.envelope.actionId)) return; // already ACK'd

      if (entry.attempts >= ACK_MAX_ATTEMPTS) {
        this.pendingMap.delete(entry.envelope.actionId);
        console.error(`[WS] sendReliable: FAILED after ${entry.attempts} attempts for ${entry.envelope.actionId}`);
        entry.reject(`No ACK after ${entry.attempts} attempts`);
      } else {
        this._attemptSendReliable(entry);
      }
    }, backoff);
  }

  onMessage(handler: MessageHandler): () => void {
    this.messageHandlers.push(handler);
    return () => {
      this.messageHandlers = this.messageHandlers.filter(h => h !== handler);
    };
  }

  onStatusChange(handler: StatusHandler): () => void {
    this.statusHandlers.push(handler);
    if (this.socket?.readyState === WebSocket.OPEN) {
      handler('CONNECTED');
    } else if (this.socket?.readyState === WebSocket.CONNECTING) {
      handler('CONNECTING');
    } else {
      handler('DISCONNECTED');
    }
    return () => {
      this.statusHandlers = this.statusHandlers.filter(h => h !== handler);
    };
  }

  private notifyStatus(status: 'CONNECTING' | 'CONNECTED' | 'DISCONNECTED' | 'ERROR' | 'RECONNECTING' | 'FAILED'): void {
    this.statusHandlers.forEach(handler => handler(status));
  }

  isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }
}

export const wsClient = new WebSocketClient();
